import { useEffect, useState } from "react";
import {
  ShieldCheck,
  ArrowLeft,
  Buildings,
  Envelope,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function PoliticaPrivacidad() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchPolicy = async () => {
      try {
        const response = await api.get("/legal/privacy-policy");

        if (!cancelled) {
          setPolicy(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("No se pudo cargar la política de privacidad");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPolicy();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <main className="min-h-screen bg-med-bg grid place-items-center p-6">
        <p className="text-med-muted">Cargando política de privacidad…</p>
      </main>
    );
  }

  if (!policy) {
    return (
      <main className="min-h-screen bg-med-bg p-6">
        <div className="max-w-3xl mx-auto py-12">
          <h1 className="font-display text-2xl font-bold text-med-text">
            Política de privacidad
          </h1>
          <p className="mt-3 text-med-muted">
            No ha sido posible cargar la política. Inténtalo de nuevo más tarde.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 mt-6 text-med-primary font-semibold"
          >
            <ArrowLeft size={18} />
            Volver al inicio de sesión
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-med-bg px-4 py-8 md:px-8 md:py-12">
      <article className="max-w-4xl mx-auto bg-med-surface border border-med-border rounded-2xl p-6 md:p-10">
        <header className="border-b border-med-border pb-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-med-primary/10 text-med-primary grid place-items-center">
              <ShieldCheck size={28} weight="duotone" />
            </div>
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-med-text">
                {policy.title || "Política de privacidad"}
              </h1>
              <p className="text-sm text-med-muted mt-1">
                Versión {policy.version || "—"}
                {policy.last_updated
                  ? ` · Actualizada el ${policy.last_updated}`
                  : ""}
              </p>
            </div>
          </div>
        </header>

        <section className="mb-8">
          <h2 className="font-display text-xl font-bold text-med-text mb-4">
            Responsable del tratamiento
          </h2>
          <LegalDetails data={policy.responsible} />
        </section>

        <section className="mb-8">
          <h2 className="font-display text-xl font-bold text-med-text mb-4">
            Proveedor de la plataforma
          </h2>
          <LegalDetails data={policy.provider} />
        </section>

        <section>
          <PolicyContent content={policy.content} />
        </section>

        <footer className="mt-10 pt-6 border-t border-med-border">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-med-primary font-semibold hover:underline"
          >
            <ArrowLeft size={18} />
            Volver al inicio de sesión
          </Link>
        </footer>
      </article>
    </main>
  );
}

function LegalDetails({ data }) {
  if (!data) return null;

  const entries = [
    ["Nombre o razón social", data.name],
    ["NIF / CIF", data.tax_id],
    ["Domicilio", data.address],
    ["Correo electrónico", data.email],
  ].filter(([, value]) => value);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-med-muted">
        Los datos legales todavía no están configurados.
      </p>
    );
  }

  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {entries.map(([label, value]) => (
        <div
          key={label}
          className="rounded-xl bg-med-bg border border-med-border p-4"
        >
          <dt className="text-xs font-semibold uppercase tracking-wider text-med-muted mb-1">
            {label}
          </dt>
          <dd className="text-sm text-med-text break-words">
            {label === "Correo electrónico" ? (
              <a
                href={`mailto:${value}`}
                className="inline-flex items-center gap-2 text-med-primary hover:underline"
              >
                <Envelope size={16} />
                {value}
              </a>
            ) : (
              value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function PolicyContent({ content }) {
  if (!content) {
    return (
      <p className="text-med-muted">
        El contenido de la política de privacidad no está disponible.
      </p>
    );
  }

  return (
    <div
      className="text-sm md:text-base text-med-text leading-7
        [&_h1]:font-display [&_h1]:text-3xl [&_h1]:font-bold
        [&_h1]:mb-6 [&_h1]:mt-8
        [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold
        [&_h2]:mt-8 [&_h2]:mb-3
        [&_h3]:font-semibold [&_h3]:text-lg
        [&_h3]:mt-6 [&_h3]:mb-2
        [&_p]:mb-4
        [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
        [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
        [&_li]:mb-1
        [&_a]:text-med-primary [&_a]:underline
        [&_strong]:font-semibold"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
